import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  AlertCircle,
  Loader,
  Search,
  Inbox,
  Star,
  FileText,
  Check,
  MoreVertical,
  Filter,
  X,
  Calendar,
  User,
  Sparkles,
} from "lucide-react";
import Button from "../../components/common/Button";
import authService from "../../api/authService";
import jobPostingApi from "../../api/jobPosting";
import useMyJobs from "../../hooks/useMyJobs";
import { useMutation } from "@tanstack/react-query";
import AutoShortlistModal from "../../components/recruiter/AutoShortlistModal";
import StatusFilter from "../../components/recruiter/StatusFilter";
import ApplicationRow from "../../components/recruiter/ApplicationRow";
import { toast } from "react-toastify";

const ViewApplicationsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [jobTitle, setJobTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [shortlistModalOpen, setShortlistModalOpen] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [jobSearchQuery, setJobSearchQuery] = useState("");
  const [debouncedJobSearch, setDebouncedJobSearch] = useState("");
  
  // Debounce job search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedJobSearch(jobSearchQuery);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [jobSearchQuery]);

  // Fetch jobs for the modal with search capability
  const { data: jobsData, isLoading: jobsLoading } = useMyJobs({
    params: {
      search: debouncedJobSearch,
      status: 'active'
    }
  });

  const autoShortlistMutation = useMutation({
    mutationFn: (shortlistData) =>
      authService.autoShortlistCandidates(shortlistData),
    onSuccess: (response) => {
      if (
        response.data?.message &&
        response.data.message.includes("No applications meet")
      ) {
        toast.warning(
          response.data.message || "No candidates found matching your criteria"
        );

        // if (response.data.criteria) {
        //     console.log('Shortlisting criteria:', response.data.criteria);
        // }
      } else {
        toast.success("Candidates shortlisted successfully!");
      }
      setShortlistModalOpen(false);
      setJobSearchQuery("");
      fetchData();
    },
    onError: (error) => {
      console.error("Failed to shortlist candidates:", error);
      toast.error(
        error.response?.data?.message || "Failed to shortlist candidates"
      );
    },
  });

  const handleAutoShortlist = (shortlistData) => {
    autoShortlistMutation.mutate(shortlistData);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (jobId && jobId !== "all") {
        params.job = jobId;
        // console.log("")
        const jobRes = await jobPostingApi.getJobPostingById(jobId);
        setJobTitle(jobRes.data.title);
      } else {
        setJobTitle("All Jobs");
      }

      const appRes = await authService.getRecruiterApplications(params);
      setApplications(appRes.data.results || []);
    } catch (err) {
      setError("Could not load applications. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);
  // console.log("applications", applications)

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusUpdate = useCallback(
    async (appId, newStatus) => {
      const originalApps = [...applications];
      setApplications((prev) =>
        prev.map((app) =>
          app.id === appId ? { ...app, status: newStatus } : app
        )
      );
      try {
        await authService.updateApplicationStatus(appId, { status: newStatus });
      } catch (error) {
        setApplications(originalApps);
        if(error?.response?.data?.details){
                const found = error.response.data.details.match(/reject/i)
                if(found) return setError("Cannot change status of an application that has been rejected.");
            }
        setError("Failed to update status. Please try again.");
      } finally{
        setTimeout(() => {
            setError("")
        }, 4000)
      }
    },
    [applications]
  );

  const filteredApplications = useMemo(() => {
    return applications
      .filter((app) => {
        if (statusFilter !== "all" && app.status !== statusFilter) return false;
        if (!searchTerm) return true;
        return app?.applicant_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [applications, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-160px)]">
        <Loader className="w-12 h-12 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <Button
          variant="link"
          onClick={() => navigate("/manage-jobs")}
          className="mb-6 text-secondary-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Job Management
        </Button>

        <div className="flex flex-wrap justify-between">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">
              Applications for "{jobTitle}"
            </h1>
            <p className="mt-1 text-secondary-600 text-sm sm:text-base">
              Review and manage candidates who have applied for this role.
            </p>
          </div>
          <div>
            <Button
              onClick={() => setShortlistModalOpen(true)}
              icon={<Sparkles className="w-4 h-4 mr-2" />}
            >
              Auto Shortlist Candidates
            </Button>
          </div>
        </div>
        {/* Enhanced Filter Section for All Screen Sizes */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by applicant name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Desktop Filter - Hidden on mobile and tablet */}
            <div className="hidden lg:block">
              <StatusFilter
                selected={statusFilter}
                onChange={setStatusFilter}
                applications={applications}
                layout="horizontal"
              />
            </div>

            {/* Tablet Filter - Scrollable horizontal on medium screens */}
            <div className="hidden md:block lg:hidden">
              <StatusFilter
                selected={statusFilter}
                onChange={setStatusFilter}
                applications={applications}
                layout="tablet"
              />
            </div>

            {/* Mobile Filter Trigger - Visible only on mobile */}
            <div className="md:hidden">
              <Button
                variant="outline"
                onClick={() => setShowFilterModal(true)}
                className="w-full justify-center"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {statusFilter !== "all" && (
                  <span className="ml-2 bg-primary-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {
                      applications.filter((app) => app.status === statusFilter)
                        .length
                    }
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Modal for Mobile */}
        {showFilterModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Filter Applications</h3>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <StatusFilter
                selected={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value);
                  setShowFilterModal(false);
                }}
                applications={applications}
                layout="vertical"
              />

              <div className="mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setStatusFilter("all");
                    setShowFilterModal(false);
                  }}
                  className="w-full justify-center"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center mb-6">
            <AlertCircle className="w-5 h-5 mr-3" />
            {error}
          </div>
        )}

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-8 sm:p-12 text-center">
            <Inbox className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              No Applications Found
            </h3>
            <p className="text-gray-600">
              There are no applications matching your current filters.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {/* Table Header - Hidden on mobile */}
            <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="col-span-5 text-sm font-semibold text-gray-600">
                APPLICANT
              </div>
              <div className="col-span-2 text-sm font-semibold text-gray-600">
                APPLIED
              </div>
              <div className="col-span-3 text-sm font-semibold text-gray-600">
                STATUS
              </div>
              <div className="col-span-2 text-sm font-semibold text-gray-600 text-right">
                ACTIONS
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {filteredApplications.map((app) => (
                <ApplicationRow
                  key={app.id}
                  app={app}
                  navigate={navigate}
                  onStatusUpdate={handleStatusUpdate}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <AutoShortlistModal
        open={shortlistModalOpen}
        onClose={() => {
          setShortlistModalOpen(false);
          setJobSearchQuery(""); // Clear search when closing
        }}
        onSubmit={handleAutoShortlist}
        jobs={jobsData?.results || []}
        isLoading={autoShortlistMutation.isPending}
        isSearching={jobsLoading}
        onSearchChange={setJobSearchQuery}
        title="Auto Shortlist Candidates"
        submitButtonText="Run Auto Shortlist"
      />
    </div>
  );
};

export default ViewApplicationsPage;
